/* global mixpanel */
import {Component, StateController} from 'panel';
import throttle from 'lodash/throttle';
import uniqWith from 'lodash/uniqWith';

import {sorted} from 'common/util/array';
import {onAnimationEnd, offAnimationEnd} from 'common/util/dom';
import {registerMPElement} from 'common/util/register-element';
import {setFlag} from 'common/report/queries/legacy-django-api/user-flags';

import {CHANGE_TYPE_MARK_AS_READ, CHANGE_TYPE_REMOVED, CHANGE_TYPE_SENT_FEEDBACK} from '../smart-hub/constants';
import {isMatchingAlert} from '../smart-hub/utils/common';
import {buildGlobalChangedAlert} from '../smart-hub/utils/globalEvents';

import {smartHubApi} from './api';
import {DisplayContext, EmptyState, EmptyStateTexts} from './constants';

import template from './index.jade';
import css from './index.styl?inline';

/** @typedef { import('./state').Alert } Alert */
/** @typedef { import('./state').ControllerState } ControllerState */

const REQUEST_LIMIT = 10;

/** @extends {StateController<ControllerState>} */
class DropdownStateController extends StateController {
  get defaultState() {
    return {
      alerts: [],
      allAlertsFetched: false,
      attachedAt: null,
      caretPosLeft: `50%`,
      dispatchedGlobalAlertsEvent: false,
      minIngestedAt: null,
      open: false,
      paginationCount: 0,
      pendingPaginationRequest: null,
      subscriptionAlertOn: false,
      withCaret: false,
    };
  }

  /* mutators */
  closeDropdown() {
    this._update({open: false});
  }

  dismissTutorial() {
    this._update({tutorialDismissed: true});
  }

  /**
   * @param {boolean} open
   */
  markOpen(open) {
    this._update({open});
  }

  /**
   * @param {Array<Alert>} alerts
   */
  updateAlerts(alerts) {
    this._update({alerts});
  }

  /**
   * @param {Alert} alert
   */
  removeAlert(alert) {
    this._update({
      alerts: this.state.alerts.filter((currAlert) => !isMatchingAlert(alert, currAlert)),
    });
  }

  markPageFetched() {
    this._update({
      paginationCount: this.state.paginationCount + 1,
      pendingPaginationRequest: null,
    });
  }

  markAllAlertsFetched() {
    this._update({allAlertsFetched: true});
  }

  markAlertsEventDispatched() {
    this._update({dispatchedGlobalAlertsEvent: true});
  }

  /**
   * @param {string} minIngestedAt
   */
  markMinIngestedAt(minIngestedAt) {
    this._update({minIngestedAt});
  }

  /**
   * @param {number} attachedAt
   */
  markAttachedAt(attachedAt) {
    this._update({attachedAt});
  }

  /**
   * @param {Promise<{string: number}>} pendingPaginationRequest
   */
  setPendingRequest(pendingPaginationRequest) {
    this._update({pendingPaginationRequest});
  }

  /**
   * @param {boolean} withCaret
   */
  setWithCaret(withCaret) {
    this._update({withCaret});
  }

  /**
   * @param {string} caretPosLeft
   */
  setCaretPosLeft(caretPosLeft) {
    this._update({caretPosLeft});
  }

  /**
   * @param {boolean} paused
   */
  setAlertSubscription(paused) {
    this._update({subscriptionAlertOn: !paused});
  }

  reset() {
    this._update(this.defaultState);
    this.markAttachedAt(new Date().getTime());
  }

  /* readers */

  /**
   * @return {number}
   */
  getAttachedAt() {
    return this.state.attachedAt;
  }

  /**
   * @return {number}
   */
  getPageCount() {
    return this.state.paginationCount;
  }

  /**
   * @return {Array<Alert>}
   */
  getAlerts() {
    return this.state.alerts;
  }

  /**
   * @return {string}
   */
  getMinIngestedAt() {
    return this.state.minIngestedAt;
  }

  /**
   * @return {boolean}
   */
  getWithCaret() {
    return this.state.withCaret;
  }

  /**
   * @return {string}
   */
  getCaretPosLeft() {
    return this.state.caretPosLeft;
  }

  /**
   * @return {boolean}
   */
  hasExplains() {
    return !!this.state.alerts.find(
      (alert) =>
        alert.anomaly && !!alert.anomaly.anomalyDetails.find((detail) => detail.explains && detail.explains.length > 0),
    );
  }

  /**
   * @return {boolean}
   */
  isOpen() {
    return this.state.open;
  }

  /**
   * @return {boolean}
   */
  isSubscriptionAlertOn() {
    return this.state.subscriptionAlertOn;
  }

  /**
   * @return {boolean}
   */
  isTutorialDismissed() {
    return this.state.tutorialDismissed;
  }

  /**
   * @return {Promise<{string: number}>}
   */
  pendingRequest() {
    return this.state.pendingPaginationRequest;
  }

  /**
   * @return {boolean}
   */
  hasPendingRequest() {
    return !!this.state.pendingPaginationRequest;
  }

  /**
   * @return {boolean}
   */
  hasDispatchedAlertsEvent() {
    return this.state.dispatchedGlobalAlertsEvent;
  }

  /**
   * @return {boolean}
   */
  canFetchAgain() {
    return !(this.state.pendingPaginationRequest || this.state.allAlertsFetched);
  }
}
/*
 * Dropdown component which is responsible for loading its own alerts from the server.
 * After attempting to load, a CustomEvent named alertsLoaded will be fired with the outcome.
 */
export default registerMPElement(
  `mp-alert-dropdown`,
  class AlertDropdown extends Component {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'bookmark-id': `number`,
        'caret-pos-left': `string`,
        'display-context': {type: `string`, default: DisplayContext.Global},
        'enable-custom-alerts': {type: `boolean`, default: false},
        'enable-subscribe-alerts': {type: `boolean`, default: false},
        'has-empty-data': {type: `boolean`, default: false},
        'has-results-keys-and-values': {type: `boolean`, default: false},
        'is-report-loading': {type: `boolean`, default: false},
        open: `boolean`,
        placement: `string`,
        placements: `json`,
        'project-id': `string`,
        'subscription-paused': `boolean`,
        'utc-offset': `string`,
        'with-caret': `boolean`,
        'workspace-id': `number`,
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,
        helpers: {
          EmptyState,
          EmptyStateTexts,
          controller: () => this.controller,
          closeTooltip: () => {
            setFlag(`DISMISSED_EXPLAINS_TOOLTIP`);
            this.trackEvent(`Smart Hub: Dismissed Explains Tutorial`, {
              'Dismiss Method': `Clicked Close Button`,
            });
            this.controller.dismissTutorial();
          },
          getSubscriptionToggleTooltipText: () => {
            return this.controller.isSubscriptionAlertOn()
              ? `Unsubscribe report from alerts`
              : `Subscribe report to alerts`;
          },
          handleScroll: () => this.fetchAlertsIfNeeded(),
          handleMousewheel: (ev) => {
            const container = ev.currentTarget;
            if (
              (ev.deltaY < 0 && container.scrollTop === 0) ||
              (ev.deltaY > 0 && container.scrollTop + container.offsetHeight >= container.scrollHeight)
            ) {
              ev.preventDefault();
              ev.stopPropagation();
            }
          },
          handleDropMenuChange: (ev) => {
            const state = ev.detail.state;
            if ([`open`, `closed`].includes(state)) {
              const open = ev.detail.state === `open`;
              this.setAttribute(`open`, JSON.stringify(open));
            }

            ev.stopPropagation();
            this.dispatchEvent(new CustomEvent(ev.type, ev));
          },
          handleChangedAlert: (ev) => {
            const {alert, data, type} = ev.detail;
            const alerts = this.controller.getAlerts().map((oldAlert) => {
              if (
                (type === CHANGE_TYPE_REMOVED || type === CHANGE_TYPE_SENT_FEEDBACK) &&
                isMatchingAlert(oldAlert, alert)
              ) {
                const isDeleted =
                  type === CHANGE_TYPE_REMOVED || (type === CHANGE_TYPE_SENT_FEEDBACK && !data.isUseful);
                return {
                  ...oldAlert,
                  ...alert,
                  ...(isDeleted ? {isDeleted, placeholderHeight: ev.target.getBoundingClientRect().height} : {}),
                };
              } else if (type === CHANGE_TYPE_MARK_AS_READ && (!data.reportId || oldAlert.reportId === data.reportId)) {
                return {
                  ...oldAlert,
                  readAtMs: data.readAtMs,
                  isRead: true,
                };
              } else {
                return oldAlert;
              }
            });
            this.controller.updateAlerts(alerts);
          },
          clickedAutomatedAlertToggle: () => {
            if (this.attr(`enable-subscribe-alerts`)) {
              this.dispatchEvent(new CustomEvent(`clickedAutomatedAlertToggle`));
            }
          },
          insertedDeletedAlert: (el, alert) => {
            const removeAlertHandler = () => {
              offAnimationEnd(el, removeAlertHandler);
              this.removeAlert(alert);
            };
            onAnimationEnd(el, removeAlertHandler);
          },
          isLoading: () => this.controller.hasPendingRequest(),
          getReportAlerts: () => {
            let alerts = this.controller.getAlerts();
            if (this.attr(`bookmark-id`)) {
              alerts = alerts.filter((alert) => alert.reportId === this.attr(`bookmark-id`));
            }
            return alerts;
          },
          shouldShowLoadingState: () => this.controller.getPageCount() === 0 && this.controller.hasPendingRequest(),
          shouldShowTutorial: () => !this.controller.isTutorialDismissed() && this.controller.hasExplains(),

          // Custom Alerts
          clickedCreateCustomAlert: () => {
            this.trackEvent(`Smart Hub: Click create custom alert`);
            if (this.attr(`enable-custom-alerts`)) {
              this.dispatchEvent(new CustomEvent(`createCustomAlertClicked`));
            } else {
              window.open(`/pricing`, `_blank`);
            }
          },
          clickedManageAlerts: (ev) => {
            const modifierKey = ev.ctrlKey || ev.metaKey;
            this.dispatchEvent(
              new CustomEvent(`manageAlerts`, {
                bubbles: true,
                composed: true,
                detail: {modifierKey},
              }),
            );
            mixpanel.track(`Smart Hub: Click manage alerts`);
          },
          getCustomAlertsDisabledMessage: () => {
            let message = null;
            if (this.attr(`is-report-loading`)) {
              message = `Report is loading`;
            } else if (this.attr(`display-context`) === DisplayContext.Insights && this.attr(`has-empty-data`)) {
              message = `Custom alerts cannot be created on reports without data.`;
            } else if (!this.attr(`enable-custom-alerts`)) {
              message = `Upgrade to create a custom alert.`;
            }
            return message;
          },
          clickedMarkAlertsAsRead: () => {
            const alerts = this.controller.getAlerts();
            const reportId = this.attr(`bookmark-id`);
            const unreadReportAlerts = alerts.filter((a) => (!reportId || a.reportId === reportId) && !a.readAtMs);
            if (unreadReportAlerts.length > 0) {
              this.fetcher.markAlertsRead({minExclusiveMs: this.controller.getAttachedAt()}).then((response) => {
                if (response.ok) {
                  const readAtMs = new Date().getTime();
                  this.updateAlertsAsRead(alerts, readAtMs);
                  this.dispatchEvent(new CustomEvent(`alertsMarkedAsRead`, {detail: {reportId}}));
                  this.dispatchEvent(buildGlobalChangedAlert(CHANGE_TYPE_MARK_AS_READ, null, {reportId, readAtMs}));
                }
              });
            }
            this.trackEvent(`Smart Hub: Click Mark all as read`);
          },
          isReportDisplayContext: () => this.attr(`display-context`) !== DisplayContext.Global,
          isDashboardDisplayContext: () => this.attr(`display-context`) === DisplayContext.Dashboard,
          isReportLoading: () => this.attr(`is-report-loading`),
          eligibleForCustomAlerts: () =>
            [DisplayContext.Funnels, DisplayContext.Insights].includes(this.attr(`display-context`)),
        },
      };
    }

    removeAlert(alert) {
      this.controller.removeAlert(alert);
      this.fetchAlertsIfNeeded();
    }

    initializeSmartHubFetcher() {
      this.fetcher = smartHubApi({
        projectId: this.attr(`project-id`),
        bookmarkId: this.attr(`bookmark-id`),
        workspaceId: this.attr(`workspace-id`),
      });
    }

    constructor() {
      super();
      this.controller = new DropdownStateController({store: this});
    }

    connectedCallback() {
      this.initializeSmartHubFetcher();
      this.handleDropdownOpenState();
      super.connectedCallback();

      this.fetchAlertsIfNeeded = throttle(() => {
        const containerEl = this.el.querySelector(`.mp-sh-alerts-container`);
        const alertEl = containerEl.querySelector(`mp-smart-hub-notification:last-of-type`);
        if (!alertEl) {
          return;
        }

        const containerElBottom = containerEl.getBoundingClientRect().bottom;
        const alertElTop = alertEl.getBoundingClientRect().top;
        // If the last alert is more than 20px above the bottom of the dropdown container.
        if (alertEl && containerElBottom - alertElTop > 20) {
          this.fetchAlerts();
        }
      }, 50);

      // For marking as read, and clear all, only affect alerts older than the current time.
      // Also, alerts that are marked as read more recently than the current time should continue
      // to be shown as unread.
      this.controller.markAttachedAt(new Date().getTime());

      this.fetchAlerts();

      this._handleGlobalChangedAlert = (ev) => {
        if (ev.detail.type === CHANGE_TYPE_MARK_AS_READ) {
          this.dispatchEvent(
            new CustomEvent(`alertsMarkedAsRead`, {
              detail: {reportId: ev.detail.data.reportId},
            }),
          );
        }
      };
      document.addEventListener(`globalChangedAlert`, this._handleGlobalChangedAlert);
    }

    disconnectedCallback() {
      super.disconnectedCallback(...arguments);
      document.removeEventListener(`globalChangedAlert`, this._handleGlobalChangedAlert);
    }

    attributeChangedCallback(attr, oldValue, newValue) {
      super.attributeChangedCallback(attr, oldValue, newValue);

      if (attr === `with-caret`) {
        this.controller.setWithCaret(this.attr(attr));
      } else if (attr === `caret-pos-left`) {
        this.controller.setCaretPosLeft(this.attr(attr));
      }

      if (attr === `subscription-paused`) {
        this.controller.setAlertSubscription(this.attr(attr));
      }

      if (!this.initialized) {
        return;
      }

      if (attr === `open`) {
        this.handleDropdownOpenState();
      } else if (attr === `placements`) {
        this.computePlacement();
      } else if (attr === `bookmark-id`) {
        // if the bookmark-id changes, reset all state in this instance.
        this.controller.reset();
        this.initializeSmartHubFetcher();
        this.fetchAlerts();
      }
    }

    handleDropdownOpenState() {
      const open = this.attr(`open`);
      if (open) {
        this.computePlacement();
        this.trackClick(1);
        this.incrementPeopleProperty(`Smart Hub: Total Clicks`);
        // TODO(mack): Try to fetch more alerts here if the last alert is visible
      }
      this.controller.markOpen(open);
    }

    getCaretOffset(placement) {
      switch (placement) {
        case `center`:
          return this.dropdownWidth / 2;
        case `left`:
        case `right`:
        default:
          return 43;
      }
    }

    computePlacement() {
      const viewportLeft = this.getBoundingClientRect().left;
      const placements = this.attr(`placements`);
      if (!placements || !placements.length) {
        if (!this.hasAttribute(`placement`)) {
          this.setAttribute(`placement`, `left`);
        }
        return;
      }

      const BUFFER_SIZE = 20;
      const documentWidth = document.body.scrollWidth;
      const left = window.pageXOffset + viewportLeft;
      let placement = placements.find((placement) => {
        let dropdownLeft;
        if (placement === `left`) {
          dropdownLeft = left - this.getCaretOffset(placement);
        } else if (placement === `right`) {
          dropdownLeft = left + this.getCaretOffset(placement) - this.dropdownWidth;
        } else if (placement === `center`) {
          dropdownLeft = this.dropdownWidth / 2;
        } else {
          console.error(`Unsupported placement ${placement}`);
          return false;
        }

        return dropdownLeft >= BUFFER_SIZE && dropdownLeft + this.dropdownWidth + BUFFER_SIZE <= documentWidth;
      });

      if (!placement) {
        console.error(`Could not find valid placement, defaulting to first placement option`);
        placement = placements[0];
      }

      this.setAttribute(`placement`, placement);
    }

    fetchAlerts() {
      if (!this.controller.canFetchAgain()) {
        return null;
      }

      this.controller.setPendingRequest(
        this.fetcher.fetchAlerts({maxExclusiveMs: this.controller.getMinIngestedAt(), limit: REQUEST_LIMIT}),
      );
      if (!this.controller.hasPendingRequest()) {
        return null;
      }

      return this.controller
        .pendingRequest()
        .then((alerts) => this.handleGetAlertsResponse(alerts))
        .catch(() => this.handleGetAlertsResponse([]));
    }

    handleGetAlertsResponse(alerts) {
      this.controller.markPageFetched();

      if (this.controller.getPageCount() > 1) {
        // This represents a manually-triggered pagination request by the user scrolling in the dropdown.
        this.trackClick(this.controller.getPageCount());
        this.incrementPeopleProperty(`Smart Hub: Total Paginations`);
      }

      if (!alerts.length || alerts.length < REQUEST_LIMIT) {
        this.controller.markAllAlertsFetched();
      }

      if (!this.controller.hasDispatchedAlertsEvent()) {
        const visibleAlerts = alerts.filter(
          (alert) => !this.attr(`bookmark-id`) || alert.reportId === this.attr(`bookmark-id`),
        );
        this.dispatchEvent(
          new CustomEvent(`alertsLoaded`, {
            detail: {
              alerts: visibleAlerts,
              loaded: visibleAlerts.length > 0,
              unread: visibleAlerts.filter((alert) => !alert.readAtMs).length,
            },
          }),
        );

        this.controller.markAlertsEventDispatched();
      }

      this.appendAlerts(alerts);
    }

    appendAlerts(alerts) {
      alerts = uniqWith(this.controller.getAlerts().concat(alerts), (a1, a2) => isMatchingAlert(a1, a2));
      alerts = sorted(alerts, {
        order: `desc`,
        transform: (alert) => {
          if (this.isHighlight(alert)) {
            return `2 - ${alert.ingestedAtMs}${alert.alertId}`;
          } else {
            return `1 - ${alert.ingestedAtMs}${alert.alertId}`;
          }
        },
      });

      alerts.forEach((alert) => ({
        ...alert,
        // Keep isRead to be true if it's already true when scrolling to load more alerts
        isRead: alert.isRead || (!!alert.readAtMs && Number(alert.readAtMs) < this.controller.getAttachedAt()),
      }));

      if (alerts.length) {
        this.controller.markMinIngestedAt(alerts[alerts.length - 1].ingestedAtMs);
      }

      this.controller.updateAlerts(alerts);
    }

    isHighlight(alert) {
      return (
        alert.anomaly && alert.anomaly.anomalyDetails.find((detail) => detail.explains && detail.explains.length > 0)
      );
    }

    trackClick(pageCount) {
      this.trackEvent(`Smart Hub: Clicked`, {page_number: pageCount});
    }

    trackEvent(eventName, extraProps = {}) {
      mixpanel.track(
        eventName,
        Object.assign(
          {
            display_context: this.attr(`display-context`),
            project_id: this.attr(`project-id`),
            bookmark_id: this.attr(`bookmark-id`),
          },
          extraProps,
        ),
      );
    }

    incrementPeopleProperty(propertyName) {
      mixpanel.people.increment(propertyName);
    }

    get dropdownWidth() {
      return 410;
    }

    updateAlertsAsRead(alerts, readAtMs) {
      alerts = alerts.map((alert) =>
        (!this.attr(`bookmark-id`) || alert.reportId === this.attr(`bookmark-id`)) && !alert.readAtMs
          ? {
              ...alert,
              readAtMs,
              isRead: true,
            }
          : alert,
      );
      this.controller.updateAlerts(alerts);
    }
  },
);
